import React, { useState } from 'react';
import { Box, Button, Divider, TextField, Typography } from '@mui/material';
import { drinkSchema, IDrink } from '../Interfaces/IDrink';
import { useCreateDrink, useUpdateDrink } from '../hooks/drinks';
import { useAlert } from '../core/AlertContext';
import { fieldSx, primaryButtonSx, secondaryButtonSx } from '../core/pvtaStyles';

interface DrinkEditProps {
    initialDrink: IDrink;
    onClose: () => void;
    showHeading?: boolean;
}

const DrinkEdit: React.FC<DrinkEditProps> = ({ initialDrink, onClose, showHeading = true }) => {
    const [drink, setDrink] = useState<IDrink>(initialDrink);
    const isCreateMode = initialDrink.id < 0;
    const { createDrink, isCreatingDrink } = useCreateDrink();
    const { updateDrink, isUpdatingDrink } = useUpdateDrink();
    const { showAlert } = useAlert();
    const isSaving = isCreatingDrink || isUpdatingDrink;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setDrink((prev) => ({
            ...prev,
            [name]: name === 'price' ? Number(value) : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const payload = drinkSchema.pick({
                name: true,
                price: true,
                remarks: true,
            }).parse(drink);

            if (isCreateMode) {
                await createDrink(payload);
                showAlert({ text: 'Getränk erfolgreich angelegt.' });
            } else {
                await updateDrink({ ...drink, ...payload });
                showAlert({ text: 'Getränk erfolgreich aktualisiert.' });
            }
            onClose();
        } catch {
            showAlert({
                text: isCreateMode ? 'Fehler beim Anlegen des Getränks.' : 'Fehler beim Aktualisieren des Getränks.',
                severity: 'error',
            });
        }
    };

    return (
        <Box sx={{ minWidth: 0 }}>
            <form onSubmit={handleSubmit}>
                {showHeading && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Details
                    </Typography>
                )}
                <TextField
                    name="name"
                    label="Name"
                    value={drink.name}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    sx={{ ...fieldSx }}
                />
                <TextField
                    name="price"
                    label="Preis"
                    type="number"
                    value={drink.price}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ ...fieldSx }}
                />
                <TextField
                    name="remarks"
                    label="Bemerkungen"
                    value={drink.remarks ?? ''}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    sx={{ ...fieldSx }}
                />
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1.5, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
                    <Button onClick={onClose} sx={{ ...secondaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                        Schließen
                    </Button>
                    <Button type="submit" disabled={isSaving} sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                        {isSaving ? 'Speichern...' : 'Speichern'}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default DrinkEdit;
